import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  startTransition,
  ReactNode,
} from 'react';

/** ATLAS office theme set — navy shell + accent variations */
export type Theme = 'light' | 'dark' | 'ocean' | 'harbor' | 'slate';

export interface ThemeColors {
  background: string;
  surface: string;
  surfaceHover: string;
  /** Header / bar background (e.g. detail view header) */
  headerBg: string;
  /** Hover class for secondary actions (e.g. Back button) */
  interactiveHover: string;

  text: string;
  textSecondary: string;
  textMuted: string;

  border: string;
  borderLight: string;
  divide: string;

  sidebarBackground: string;
  sidebarText: string;
  sidebarTextHover: string;
  sidebarItemHover: string;
  sidebarActive: string;
  sidebarActiveText: string;
  sidebarBorder: string;

  topbarBackground: string;
  topbarText: string;
  topbarBorder: string;

  primary: string;
  primaryHover: string;
  primaryText: string;
  primaryFg: string;
  primaryBorder: string;
  primaryBorderAll: string;
  primaryChecked: string;
  primaryCheckedHover: string;

  accent: string;
  accentHover: string;

  success: string;
  warning: string;
  error: string;
  info: string;
}

const NAVY_SIDEBAR = 'bg-gradient-to-b from-[#0a1424] to-[#143052]';

const themes: Record<Theme, ThemeColors> = {
  /** Light + steel navy (office login / logo) */
  light: {
    background: 'bg-slate-50',
    surface: 'bg-white',
    surfaceHover: 'bg-slate-100',
    headerBg: 'bg-slate-50',
    interactiveHover: 'hover:bg-slate-100',
    text: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    border: 'border-slate-200',
    borderLight: 'border-slate-100',
    divide: 'divide-slate-100',
    sidebarBackground: NAVY_SIDEBAR,
    sidebarText: 'text-slate-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-[#3d5a80] to-[#6b8fb8]',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-slate-700/60',
    topbarBackground: 'bg-white',
    topbarText: 'text-slate-900',
    topbarBorder: 'border-slate-200',
    primary: 'bg-[#3d5a80]',
    primaryHover: 'bg-[#2c4a6e]',
    primaryText: 'text-white',
    primaryFg: 'text-[#2c4a6e]',
    primaryBorder: 'border-l-[#3d5a80]',
    primaryBorderAll: 'border-[#3d5a80]',
    primaryChecked: 'peer-checked:bg-[#3d5a80]',
    primaryCheckedHover: 'peer-checked:hover:bg-[#2c4a6e]',
    accent: 'bg-[#6b8fb8]',
    accentHover: 'bg-[#5a7fa8]',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-900',
    error: 'bg-red-100 text-red-800',
    info: 'bg-sky-100 text-sky-900',
  },

  /** Dark + steel navy — clear elevation, readable muted text */
  dark: {
    background: 'bg-[#080d18]',
    surface: 'bg-[#121a2b]',
    surfaceHover: 'bg-[#1a2438]',
    headerBg: 'bg-[#0e1524]',
    interactiveHover: 'hover:bg-[#1a2438]',
    text: 'text-slate-100',
    textSecondary: 'text-slate-300',
    textMuted: 'text-slate-400',
    border: 'border-white/10',
    borderLight: 'border-white/[0.06]',
    divide: 'divide-white/10',
    sidebarBackground: 'bg-gradient-to-b from-[#060a14] via-[#0a1424] to-[#0f1c30]',
    sidebarText: 'text-slate-300',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/[0.06] hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-[#3d5a80] to-[#6b8fb8]',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-white/10',
    topbarBackground: 'bg-[#0e1524]',
    topbarText: 'text-slate-100',
    topbarBorder: 'border-white/10',
    primary: 'bg-[#6b8fb8]',
    primaryHover: 'bg-[#7a9cc4]',
    primaryText: 'text-white',
    primaryFg: 'text-[#9bb4d0]',
    primaryBorder: 'border-l-[#6b8fb8]',
    primaryBorderAll: 'border-[#6b8fb8]',
    primaryChecked: 'peer-checked:bg-[#6b8fb8]',
    primaryCheckedHover: 'peer-checked:hover:bg-[#7a9cc4]',
    accent: 'bg-[#5a7fa8]',
    accentHover: 'bg-[#6b8fb8]',
    success: 'bg-emerald-500/15 text-emerald-300',
    warning: 'bg-amber-500/15 text-amber-200',
    error: 'bg-red-500/15 text-red-300',
    info: 'bg-sky-500/15 text-sky-300',
  },

  /** Cool steel-blue accents on light surfaces */
  ocean: {
    background: 'bg-slate-50',
    surface: 'bg-white',
    surfaceHover: 'bg-slate-100',
    headerBg: 'bg-slate-50',
    interactiveHover: 'hover:bg-slate-100',
    text: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    border: 'border-slate-200',
    borderLight: 'border-slate-100',
    divide: 'divide-slate-100',
    sidebarBackground: 'bg-gradient-to-b from-[#0a1628] to-[#1e4a6e]',
    sidebarText: 'text-slate-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-sky-600 to-sky-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-slate-700/60',
    topbarBackground: 'bg-white',
    topbarText: 'text-slate-900',
    topbarBorder: 'border-slate-200',
    primary: 'bg-sky-600',
    primaryHover: 'bg-sky-700',
    primaryText: 'text-white',
    primaryFg: 'text-sky-700',
    primaryBorder: 'border-l-sky-500',
    primaryBorderAll: 'border-sky-500',
    primaryChecked: 'peer-checked:bg-sky-600',
    primaryCheckedHover: 'peer-checked:hover:bg-sky-700',
    accent: 'bg-sky-700',
    accentHover: 'bg-sky-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-900',
    error: 'bg-red-100 text-red-800',
    info: 'bg-sky-100 text-sky-900',
  },

  /** Sea / harbor teal accents */
  harbor: {
    background: 'bg-slate-50',
    surface: 'bg-white',
    surfaceHover: 'bg-slate-100',
    headerBg: 'bg-slate-50',
    interactiveHover: 'hover:bg-slate-100',
    text: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    border: 'border-slate-200',
    borderLight: 'border-slate-100',
    divide: 'divide-slate-100',
    sidebarBackground: 'bg-gradient-to-b from-[#042022] to-[#0c3d40]',
    sidebarText: 'text-slate-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-teal-600 to-teal-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-teal-900/40',
    topbarBackground: 'bg-white',
    topbarText: 'text-slate-900',
    topbarBorder: 'border-slate-200',
    primary: 'bg-teal-600',
    primaryHover: 'bg-teal-700',
    primaryText: 'text-white',
    primaryFg: 'text-teal-700',
    primaryBorder: 'border-l-teal-500',
    primaryBorderAll: 'border-teal-500',
    primaryChecked: 'peer-checked:bg-teal-600',
    primaryCheckedHover: 'peer-checked:hover:bg-teal-700',
    accent: 'bg-teal-700',
    accentHover: 'bg-teal-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-900',
    error: 'bg-red-100 text-red-800',
    info: 'bg-teal-50 text-teal-900',
  },

  /** Neutral charcoal / slate — minimal */
  slate: {
    background: 'bg-zinc-50',
    surface: 'bg-white',
    surfaceHover: 'bg-zinc-100',
    headerBg: 'bg-zinc-50',
    interactiveHover: 'hover:bg-zinc-100',
    text: 'text-zinc-900',
    textSecondary: 'text-zinc-700',
    textMuted: 'text-zinc-500',
    border: 'border-zinc-200',
    borderLight: 'border-zinc-100',
    divide: 'divide-zinc-100',
    sidebarBackground: 'bg-gradient-to-b from-zinc-950 to-zinc-800',
    sidebarText: 'text-zinc-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-zinc-600',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-zinc-700',
    topbarBackground: 'bg-white',
    topbarText: 'text-zinc-900',
    topbarBorder: 'border-zinc-200',
    primary: 'bg-zinc-800',
    primaryHover: 'bg-zinc-900',
    primaryText: 'text-white',
    primaryFg: 'text-zinc-800',
    primaryBorder: 'border-l-zinc-700',
    primaryBorderAll: 'border-zinc-700',
    primaryChecked: 'peer-checked:bg-zinc-800',
    primaryCheckedHover: 'peer-checked:hover:bg-zinc-900',
    accent: 'bg-zinc-700',
    accentHover: 'bg-zinc-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-900',
    error: 'bg-red-100 text-red-800',
    info: 'bg-zinc-100 text-zinc-800',
  },
};

const scrollbarVars: Record<Theme, { track: string; thumb: string; thumbHover: string }> = {
  light: { track: '#f1f5f9', thumb: '#6b8fb8', thumbHover: '#3d5a80' },
  dark: { track: '#0e1524', thumb: '#5a7fa8', thumbHover: '#6b8fb8' },
  ocean: { track: '#f1f5f9', thumb: '#38bdf8', thumbHover: '#0284c7' },
  harbor: { track: '#f1f5f9', thumb: '#2dd4bf', thumbHover: '#0f766e' },
  slate: { track: '#f4f4f5', thumb: '#71717a', thumbHover: '#52525b' },
};

/** Map legacy theme ids saved in localStorage to the new set */
const LEGACY_THEME_MAP: Record<string, Theme> = {
  blue: 'ocean',
  green: 'harbor',
  purple: 'light',
  macos: 'slate',
  auto: 'light',
};

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Office',
  dark: 'Midnight',
  ocean: 'Ocean',
  harbor: 'Harbor',
  slate: 'Slate',
};

export const THEME_DESCRIPTIONS: Record<Theme, string> = {
  light: 'Navy shell · steel accents',
  dark: 'Deep night · steel blue',
  ocean: 'Steel blue · cool clarity',
  harbor: 'Sea teal · onboard feel',
  slate: 'Charcoal · quiet minimal',
};

const AVAILABLE_THEMES: Theme[] = ['light', 'dark', 'ocean', 'harbor', 'slate'];

export function resolveTheme(raw: string | null | undefined): Theme {
  if (raw && raw in themes) return raw as Theme;
  if (raw && LEGACY_THEME_MAP[raw]) return LEGACY_THEME_MAP[raw];
  return 'light';
}

const getDefaultSidebarCollapsedForTheme = (_theme: Theme): boolean => false;

interface ThemeContextType {
  theme: Theme;
  colors: ThemeColors;
  setTheme: (theme: Theme) => void;
  availableThemes: Theme[];
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => resolveTheme(localStorage.getItem('app-theme')));

  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const saved = localStorage.getItem('app-sidebar-collapsed');
    if (saved === 'true' || saved === 'false') return saved === 'true';
    return getDefaultSidebarCollapsedForTheme(resolveTheme(localStorage.getItem('app-theme')));
  });

  const setTheme = useCallback((newTheme: Theme) => {
    const collapsed = getDefaultSidebarCollapsedForTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    localStorage.setItem('app-sidebar-collapsed', String(collapsed));
    startTransition(() => {
      setThemeState(newTheme);
      setSidebarCollapsedState(collapsed);
    });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    localStorage.setItem('app-sidebar-collapsed', String(collapsed));
    setSidebarCollapsedState(collapsed);
  }, []);

  const colors = themes[theme];
  const availableThemes = AVAILABLE_THEMES;
  const scrollbar = scrollbarVars[theme];

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const value = useMemo(
    () => ({ theme, colors, setTheme, availableThemes, sidebarCollapsed, setSidebarCollapsed }),
    [theme, colors, setTheme, availableThemes, sidebarCollapsed, setSidebarCollapsed],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div
        className={colors.background}
        data-theme={theme}
        style={
          {
            '--scrollbar-track': scrollbar.track,
            '--scrollbar-thumb': scrollbar.thumb,
            '--scrollbar-thumb-hover': scrollbar.thumbHover,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
